import { useState } from "react";
import type { EvalFailureModeTag, FeedbackTraceResponse } from "../types";
import { buildThreadSourceRef } from "../utils/buildThreadSourceRef";
import { useAnnotationHistory } from "./useAnnotationHistory";
import { useCreateAnnotation } from "./useCreateAnnotation";
import { useEvalAnnotation } from "./useEvalAnnotation";
import { useMarkAnnotationSanitised } from "./useMarkAnnotationSanitised";
import { useMarkAnnotationExported } from "./useMarkAnnotationExported";
import { useReviewerIdentity } from "./useReviewerIdentity";
import { useUpdateAnnotationReview } from "./useUpdateAnnotationReview";

function getMutationErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Something went wrong. Please try again.";
}

export function useAnnotationWorkflow(trace: FeedbackTraceResponse) {
  const reviewerIdentity = useReviewerIdentity();
  const [selectedAnnotationId, setSelectedAnnotationId] = useState<
    string | undefined
  >();
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [sanitiseError, setSanitiseError] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  const assistantMessageIndex = trace.selected_assistant_message_index;
  const workspaceId = trace.workspace_id;
  const threadId = trace.thread_id;

  const annotationQuery = useEvalAnnotation({
    workspaceId,
    annotationId: selectedAnnotationId,
  });
  const historyQuery = useAnnotationHistory({
    workspaceId,
    threadId,
    assistantMessageIndex,
  });

  const createMutation = useCreateAnnotation();
  const reviewMutation = useUpdateAnnotationReview();
  const sanitiseMutation = useMarkAnnotationSanitised();
  const exportMutation = useMarkAnnotationExported();

  const isSubmitting =
    createMutation.isPending ||
    reviewMutation.isPending ||
    sanitiseMutation.isPending ||
    exportMutation.isPending;

  async function createAndStartReview() {
    setReviewError(null);

    try {
      const created = await createMutation.mutateAsync({
        source_ref: buildThreadSourceRef(trace.item_ref),
        reviewer_assigned: reviewerIdentity,
      });
      setSelectedAnnotationId(created.annotation_id);

      await reviewMutation.mutateAsync({
        workspaceId,
        threadId,
        assistantMessageIndex,
        annotationId: created.annotation_id,
        body: {
          status: "in_review",
          reviewer_assigned: reviewerIdentity,
        },
      });
    } catch (error) {
      setReviewError(getMutationErrorMessage(error));
    }
  }

  async function startReview() {
    if (!selectedAnnotationId) {
      return;
    }

    setReviewError(null);

    try {
      await reviewMutation.mutateAsync({
        workspaceId,
        threadId,
        assistantMessageIndex,
        annotationId: selectedAnnotationId,
        body: {
          status: "in_review",
          reviewer_assigned: reviewerIdentity,
        },
      });
    } catch (error) {
      setReviewError(getMutationErrorMessage(error));
    }
  }

  async function approve(input: {
    reviewerNotes: string;
    failureModeTags: EvalFailureModeTag[];
  }) {
    if (!selectedAnnotationId) {
      return;
    }

    setReviewError(null);

    try {
      await reviewMutation.mutateAsync({
        workspaceId,
        threadId,
        assistantMessageIndex,
        annotationId: selectedAnnotationId,
        body: {
          status: "approved",
          reviewer_notes: input.reviewerNotes || null,
          failure_mode_tags: input.failureModeTags,
          reviewer_assigned: reviewerIdentity,
        },
      });
    } catch (error) {
      setReviewError(getMutationErrorMessage(error));
    }
  }

  async function reject(input: {
    reviewerNotes: string;
    failureModeTags: EvalFailureModeTag[];
  }) {
    if (!selectedAnnotationId) {
      return;
    }

    setReviewError(null);

    try {
      await reviewMutation.mutateAsync({
        workspaceId,
        threadId,
        assistantMessageIndex,
        annotationId: selectedAnnotationId,
        body: {
          status: "rejected",
          reviewer_notes: input.reviewerNotes || null,
          failure_mode_tags: input.failureModeTags,
          reviewer_assigned: reviewerIdentity,
        },
      });
    } catch (error) {
      setReviewError(getMutationErrorMessage(error));
    }
  }

  async function markSanitised(input: { sanitisationNotes: string }) {
    if (!selectedAnnotationId) {
      return;
    }

    setSanitiseError(null);

    try {
      await sanitiseMutation.mutateAsync({
        workspaceId,
        threadId,
        assistantMessageIndex,
        annotationId: selectedAnnotationId,
        body: {
          sanitised_by: reviewerIdentity,
          sanitisation_notes: input.sanitisationNotes || null,
        },
      });
    } catch (error) {
      setSanitiseError(getMutationErrorMessage(error));
    }
  }

  async function markExported(input: { exportedToGoldenId: string }) {
    if (!selectedAnnotationId) {
      return;
    }

    setExportError(null);

    try {
      await exportMutation.mutateAsync({
        workspaceId,
        threadId,
        assistantMessageIndex,
        annotationId: selectedAnnotationId,
        body: {
          exported_to_golden_id: input.exportedToGoldenId,
        },
      });
    } catch (error) {
      setExportError(getMutationErrorMessage(error));
    }
  }

  return {
    selectedAnnotationId,
    setSelectedAnnotationId,
    annotation: annotationQuery.data,
    isLoadingAnnotation:
      Boolean(selectedAnnotationId) &&
      (annotationQuery.isLoading || annotationQuery.isFetching),
    historyItems: historyQuery.data?.items ?? [],
    isLoadingHistory: historyQuery.isLoading,
    reviewerIdentity,
    isSubmitting,
    isCreating: createMutation.isPending,
    reviewErrorMessage: reviewError,
    sanitiseErrorMessage: sanitiseError,
    exportErrorMessage: exportError,
    createAndStartReview,
    startReview,
    approve,
    reject,
    markSanitised,
    markExported,
  };
}
